import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useState
} from 'react';
import ClayIcon from '@clayui/icon';
import './Pagination.css';

interface PaginationProps {
    defaultActive: number;
    totalItems: number;
    initialDelta?: number;
    deltas?: { label: number; href?: string }[];
    onDeltaChange?: (delta: number) => void;
    onPageChange?: (page: number) => void;
    currentPage:number;
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
}

const CustomPagination: React.FC<PaginationProps> = forwardRef(
    (
        {
            defaultActive,
            totalItems,
            initialDelta = 10,
            deltas = [{ label: 4 }, { label: 8 }, { label: 20 },{label:40},{label:60}],
            onDeltaChange,
            onPageChange,
            currentPage,
            setCurrentPage
        },
        ref
    ) => {
        const [delta, setDelta] = useState(initialDelta);
        const totalPages = Math.max(1, Math.ceil(totalItems / delta)); // Ensure at least 1 page
        const startIndex = (currentPage - 1) * delta + 1;
        const endIndex = Math.min(currentPage * delta, totalItems);

        const getPager = (page: number) => {
            const displayedPages: (number | string)[] = [];

            // Always include the first page
            displayedPages.push(1);
            if (totalPages > 1) {
       
                // Add ellipses if the current page is far from the first page
                if (page > 3) {
                    displayedPages.push('...');
                }

                // Show current page and 2 pages before and after it
                const startPage = Math.max(2, page - 1);
                const endPage = Math.min(totalPages - 1, page + 1);

                for (let i = startPage; i <= endPage; i++) {
                    displayedPages.push(i);
                }

                // Add ellipses if the current page is far from the last page
                if (page < totalPages - 2) {
                    displayedPages.push('...');
                }

                // Always include the last page
                displayedPages.push(totalPages);
            }

            return displayedPages;
        };

        const [pages, setPages] = useState(getPager(currentPage));

        useEffect(() => {
            setPages(getPager(currentPage));
        }, [currentPage, delta, totalItems]);

        useImperativeHandle(ref, () => ({
            reset
        }));

        const setPage = (page: number) => {
            if (page < 1 || page > totalPages) return; // Prevent invalid pages
            setCurrentPage(page);
            setPages(getPager(page));
            onPageChange && onPageChange(page); // Update pages when changing the page
        };

        const reset = () => {
            setPage(1);
        };

        const nextPage = () => {
            setPage(currentPage + 1);
        };

        const previousPage = () => {
            setPage(currentPage - 1);
        };

        const handleDeltaChange = (newDelta: number) => {
            setDelta(newDelta);
            if (onDeltaChange) {
                onDeltaChange(newDelta);
            }
            setCurrentPage(1); // Reset page to 1 when delta changes
        };
 
        // Always display pagination even with one page
        if (!pages) {
            return null;
        }

        return (
            <div className="pagination-bar mt-4">
                <div className="dropdown pagination-items-per-page">
                    <button
                        aria-expanded="false"
                        aria-haspopup="true"
                        className="dropdown-toggle"
                        data-toggle="dropdown"
                    >
                        {initialDelta} Entries
                        <svg
                            width="25"
                            height="25"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M9.99992 11.9838C9.90992 11.9838 9.82714 11.969 9.75158 11.9396C9.67603 11.9101 9.60186 11.859 9.52908 11.7863L5.87158 8.12958C5.79381 8.05181 5.75214 7.95625 5.74658 7.84292C5.74103 7.72958 5.7827 7.62847 5.87158 7.53958C5.96047 7.45069 6.05881 7.40625 6.16658 7.40625C6.27436 7.40625 6.3727 7.45069 6.46158 7.53958L9.99992 11.0779L13.5383 7.53958C13.616 7.46181 13.7116 7.42014 13.8249 7.41458C13.9383 7.40903 14.0394 7.45069 14.1283 7.53958C14.2171 7.62847 14.2616 7.72681 14.2616 7.83458C14.2616 7.94236 14.2171 8.04069 14.1283 8.12958L10.4708 11.7863C10.3985 11.859 10.3244 11.9101 10.2483 11.9396C10.1727 11.969 10.0899 11.9838 9.99992 11.9838Z"
                                fill="#003764"
                            />
                        </svg>
                    </button>
                    <ul className="dropdown-menu dropdown-menu-top">
                        {deltas.map((d) => (
                            <li key={d.label}>
                                <div
                                    className={`dropdown-item ${
                                        d.label === delta ? 'active' : ''
                                    }`}
                                    onClick={() => handleDeltaChange(d.label)}
                                >
                                    {d.label}
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="pagination-results">
                Showing {startIndex} to {endIndex} of {totalItems} entries.
                </div>
                <div className="pagination-number-text">
                    <ul className="pagination">
                        <li className="page-item">
                            <a
                                onClick={previousPage}
                                className="page-link"
                                role="button"
                            >
                                <ClayIcon symbol="angle-left" />
                            </a>
                        </li>

                        {pages.map((page, index) => (
                            <li
                                key={index}
                                className={
                                    typeof page === 'number' &&
                                    currentPage === page
                                        ? 'active page-item'
                                        : 'page-item'
                                }
                            >
                                {typeof page === 'number' ? (
                                    <a
                                        onClick={() => setPage(page)}
                                        className="page-link"
                                    >
                                        {page}
                                    </a>
                                ) : (
                                    <span className="page-link disabled">
                                        {page}
                                    </span>
                                )}
                            </li>
                        ))}

                        <li className="page-item">
                            <a
                                onClick={nextPage}
                                className="page-link"
                                role="button"
                            >
                                <ClayIcon symbol="angle-right" />
                            </a>
                        </li>
                    </ul>
                </div>
            </div>
        );
    }
);

export default CustomPagination;
